import { useQuery } from '@tanstack/react-query';
import { getUserEntityPermissions } from 'config/requests/userEntityPermissions';
import { useTypedTranslate } from 'providers/i18n/hooks/useTypedTranslate';
import { queryClient } from 'providers/queryClient';
import {
  Form,
  LinearProgress,
  RecordContextProvider,
  useDataProvider,
  useNotify,
} from 'react-admin';
import { Button, SaveButton } from 'shared/mui/Buttons';
import { CardActionsButtons, CardHeader } from 'shared/mui/Card';
import { CardContent } from 'shared/mui/Card/CardContent/CardContent';
import { Dialog } from 'shared/mui/Dialogs';
import { CloseIconButton } from 'shared/mui/IconButtons';
import { UserEntityPermissionsDialogForm } from 'shared/widgets/UserEntityPermissionsDialog/components/UserEntityPermissionsForm/UserEntityPermissionsDialogForm';
import { USER_ENTITY_PERMISSIONS_RESOURCES } from 'shared/widgets/UserEntityPermissionsDialog/constants/userEntityPermissionsNames';
import { UserEntityPermissionFormValues } from 'shared/widgets/UserEntityPermissionsDialog/UserEntityPermissionsDialogCreate/UserEntityPermissionsDialogCreate';
import { UserEntityPermissionsNames } from 'types/embedded/userEntityPermission';
import { formatInputToTimestamp } from 'utils/date';

type Props = {
  open: boolean;
  onClose: () => void;
  title: string;
  recordId: string;
  recordName: string;
  entityName: UserEntityPermissionsNames;
};

export const UserEntityPermissionsDialogEdit: React.FC<Props> = ({
  open,
  onClose,
  title,
  recordId,
  recordName,
  entityName,
}) => {
  const translate = useTypedTranslate();

  const dataProvider = useDataProvider();
  const notify = useNotify();
  const { data: permissions, isLoading } = useQuery({
    queryKey: ['user_entity_permissions', recordId],
    queryFn: () =>
      getUserEntityPermissions({
        entity: entityName,
        rowId: Number(recordId),
      }),
  });

  const transformPermissions = permissions?.map((permission) => ({
    ...permission,
    expiredAt: permission?.expiredAt ? permission?.expiredAt * 1000 : null,
  }));

  const onSubmit = async (formValues: UserEntityPermissionFormValues) => {
    const createdPermissions = formValues.permissions.filter((permission) => !permission.id);
    if (!createdPermissions.length) {
      await queryClient.invalidateQueries({
        queryKey: [USER_ENTITY_PERMISSIONS_RESOURCES[entityName]],
      });
      onClose();
      return;
    }
    try {
      await Promise.all(
        createdPermissions.map((createdPermission) =>
          dataProvider.create('user_entity_permissions', {
            data: {
              ...createdPermission,
              expiredAt: formatInputToTimestamp(createdPermission?.expiredAt, { unit: 'seconds' }),
            },
          }),
        ),
      );
      await queryClient.invalidateQueries({
        queryKey: [USER_ENTITY_PERMISSIONS_RESOURCES[entityName]],
      });
      notify(translate('app.merchantsShowNotifications.allAccessSettingsCreated'), {
        type: 'success',
      });
      onClose();
    } catch (error) {
      notify((error as Error)?.message || translate('app.common.error'), { type: 'error' });
    }
  };

  return (
    <Dialog maxWidth="sm" onClose={onClose} open={open}>
      <RecordContextProvider value={{ permissions: transformPermissions }}>
        <Form onSubmit={onSubmit as any}>
          <CardHeader
            appendActions={[<CloseIconButton key="close-button" onClose={onClose} />]}
            title={title}
            titleSize="smallBold"
          />
          <CardContent sx={{ width: '600px', boxSizing: 'border-box' }}>
            {isLoading ? (
              <LinearProgress sx={{ width: '100%' }} />
            ) : (
              <UserEntityPermissionsDialogForm
                entityName={entityName}
                recordId={recordId}
                recordName={recordName}
              />
            )}
          </CardContent>
          <CardActionsButtons
            rightActionsSlot={[
              <Button
                key="cancel"
                label={translate('app.buttons.cancel')}
                onClick={onClose}
                variant="outlined"
              />,
              <SaveButton key="create" label={translate('app.buttons.save')} />,
            ]}
          />
        </Form>
      </RecordContextProvider>
    </Dialog>
  );
};
